import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { UserAction } from '~/eds/src/types/UserAction';
import { FieldModal } from '~/features/data-fields/FieldModal';
import { SectionCard } from '~/features/data-fields/SectionCard';
import { SectionModal } from '~/features/data-fields/SectionModal';
import { DataFieldSection } from '~/features/data-fields/types';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';
import { PilotId } from '~/types/PilotId';

const Page = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { params } = useRouting();
  const { clientId } = params;

  const enableDocumentXrayDataFields = useFlag(FlagType.FieldAi);

  const sections = useSelector(slices.dataFields.selectors.selectSections);

  const [sectionCollapseStates, setSectionCollapseStates] = useState<
    Record<PilotId, boolean>
  >({});

  const {
    data: initialData,
    isLoading,
    isFetching,
    error,
  } = api.endpoints.getClientDataFieldSections.useQuery(
    {
      clientId: clientId as string,
      options: { includeXRayDetails: enableDocumentXrayDataFields },
    },
    {
      skip: !clientId,
    },
  );

  const [reorderDataFields] = api.endpoints.reorderDataFields.useMutation();

  useEffect(() => {
    if (initialData?.results) {
      dispatch(slices.dataFields.actions.setSections(initialData.results));
    }
  }, [initialData]);

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'DataFields.navTitle',
        defaultMessage: 'Data Fields',
        description: 'Navigation title for data fields page',
      }),
      pathname: RoutePathType.AdminConsoleClientDataFields,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId,
    },
    intl,
  });

  const actions: UserAction[] = [
    {
      text: intl.formatMessage({
        id: 'DataFields.reorderSectionsAction',
        defaultMessage: 'Reorder Sections',
        description: 'Reorder sections action button text',
      }),
      onClick: () => dispatch(slices.dataFields.actions.setAction('reorder')),
    },
    {
      text: intl.formatMessage({
        id: 'DataFields.addSectionsAction',
        defaultMessage: 'Add Sections',
        description: 'Add sections action button text',
      }),
      onClick: () => dispatch(slices.dataFields.actions.setAction('add')),
    },
  ];

  const moreActions: UserAction[] = [
    {
      text: intl.formatMessage({
        id: 'DataFields.collapseAllSectionsAction',
        defaultMessage: 'Collapse All Sections',
        description: 'Collapse all sections action text',
      }),

      onClick: () => {
        const newStates = Object.fromEntries(
          sections.map((section) => [section.id, true]),
        );
        setSectionCollapseStates(newStates);
      },
    },
    {
      text: intl.formatMessage({
        id: 'DataFields.expandAllSectionsAction',
        defaultMessage: 'Expand All Sections',
        description: 'Expand all sections action text',
      }),

      onClick: () => {
        const newStates = Object.fromEntries(
          sections.map((section) => [section.id, false]),
        );
        setSectionCollapseStates(newStates);
      },
    },
  ];

  const handleFieldsModal = (
    modal: string,
    section: DataFieldSection,
    selectedItem: any,
  ) => {
    const modalActionMap = {
      modal_delete: 'delete-field' as const,
      modal_custom_field: 'edit-field' as const,
      modal_custom_field_move: 'move-field' as const,
    };

    const action = modalActionMap[modal as keyof typeof modalActionMap];
    if (action) {
      dispatch(slices.dataFields.actions.setAction(action));
      dispatch(slices.dataFields.actions.setSection(section));
      dispatch(slices.dataFields.actions.setField(selectedItem));
    }
  };

  const handleFieldReorder = (
    data: { id: number; position: number }[],
    sectionId: number,
  ) => {
    reorderDataFields({
      sectionId,
      fieldOrder: data,
    })
      ?.unwrap()
      .then(() => {
        toast({
          message: intl.formatMessage({
            id: 'DataFields.fieldReorderedSuccess',
            defaultMessage: 'The field has been reordered.',
            description: 'Success message when field is reordered',
          }),
          status: 'success',
        });
      })
      .catch(() => {
        toast({
          message: intl.formatMessage({
            id: 'DataFields.fieldReorderError',
            defaultMessage: 'An error occurred while reordering the field.',
            description: 'Error message when field reorder fails',
          }),
          status: 'danger',
        });
      });
  };

  return (
    <PageLayout
      actions={actions}
      moreActions={moreActions}
      nav={nav}
      loadingContent={{
        isLoading: isLoading || isFetching,
        message: intl.formatMessage({
          id: 'DataFields.loadingMessage',
          defaultMessage: 'Loading contents',
          description: 'Loading message while fetching data',
        }),
      }}
      title={intl.formatMessage({
        id: 'DataFields.pageTitle',
        defaultMessage: 'Data Fields',
        description: 'Data fields page title',
      })}
      placeholderContent={
        error
          ? {
              message: intl.formatMessage({
                id: 'DataFields.loadingErrorMessage',
                defaultMessage:
                  'An error occurred while loading the data fields.',
                description: 'Error message when data loading fails',
              }),
            }
          : undefined
      }
    >
      {sections.map((section: DataFieldSection) => {
        return (
          <SectionCard
            key={section.id}
            section={section}
            isCollapsed={sectionCollapseStates[section.id] ?? false}
            onToggle={(collapsed) =>
              setSectionCollapseStates((prev) => ({
                ...prev,
                [section.id]: collapsed,
              }))
            }
            onFieldsModal={handleFieldsModal}
            onFieldReorder={handleFieldReorder}
          />
        );
      })}
      <FieldModal clientId={clientId ?? ''} />
      <SectionModal clientId={clientId ?? ''} />
    </PageLayout>
  );
};

export default Page;
